import type { $Fetch } from 'ofetch';
import type {
  DocumentoTipoGateway, ListDocumentoTiposAdminParams,
} from '@/application/port/out/DocumentoTipoGateway';
import type { DocumentoTipo, DocumentoTipoCommand } from '@/domain/model/Documento';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/**
 * HTTP gateway pro CRUD admin de DocumentoTipo (acadadmin slice).
 * Endpoint root: `/api/v1/admin/documento-tipos`. Authority: `acadadmin:documento_tipo:read|manage`.
 *
 * @since G.5.1
 */
export class HttpDocumentoTipoGateway implements DocumentoTipoGateway {
  constructor(private readonly client: $Fetch) {}

  async list(params: ListDocumentoTiposAdminParams): Promise<Page<DocumentoTipo>> {
    const query: Record<string, string | number> = {
      page: params.page ?? 0,
      size: params.size ?? 50,
    };
    if (params.categoria) query.categoria = params.categoria;
    const res = await this.client<ApiResponse<Page<DocumentoTipo>>>(
      '/api/v1/admin/documento-tipos', { method: 'GET', params: query });
    assertApiSuccess(res);
    return res.data;
  }

  async get(id: string): Promise<DocumentoTipo> {
    const res = await this.client<ApiResponse<DocumentoTipo>>(
      `/api/v1/admin/documento-tipos/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async create(cmd: DocumentoTipoCommand): Promise<DocumentoTipo> {
    const res = await this.client<ApiResponse<DocumentoTipo>>(
      '/api/v1/admin/documento-tipos', { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async update(id: string, cmd: DocumentoTipoCommand): Promise<DocumentoTipo> {
    const res = await this.client<ApiResponse<DocumentoTipo>>(
      `/api/v1/admin/documento-tipos/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async deactivate(id: string): Promise<void> {
    // DELETE 204 No Content — não chamar assertApiSuccess (memória [[ava-delete-204]]).
    await this.client(
      `/api/v1/admin/documento-tipos/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}
